import { ReactNode, ReactPortal, useEffect } from "react";
import { ReactQueryProvider } from "./ReactQuery";
import { AccountProvider } from "./Account";
import { StoreProvider } from "./Store";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { useUTM } from "~/hooks/useUTM";
import { usePartnerStack } from "~/hooks/usePartnerStack";
import { NuqsAdapter } from "nuqs/adapters/react";
import { useLanguageSync } from "~/hooks/useLanguageSync";

type Props = {
  children: ReactNode | ReactPortal;
};

// Language sync component to be used inside the ReactQueryProvider
const LanguageSyncProvider = ({ children }: Props) => {
  useLanguageSync();
  return <>{children}</>;
};

export const ContextWrapper = ({ children }: Props) => {
  const { init } = usePartnerStack();

  useUTM();

  useEffect(() => {
    const loadPartnerStack = () => {
      const script = init();
      if (!script) return;
      document.body.appendChild(script);
    };

    // Defer PartnerStack loading to idle time to avoid blocking main thread
    if ("requestIdleCallback" in window) {
      const idleId = requestIdleCallback(loadPartnerStack);
      return () => cancelIdleCallback(idleId);
    } else {
      const timerId = setTimeout(loadPartnerStack, 2500);
      return () => clearTimeout(timerId);
    }
  }, []);

  return (
    <GoogleOAuthProvider clientId={import.meta.env.VITE_GOOGLE_OAUTH_ID}>
      <AccountProvider>
        <StoreProvider>
          <ReactQueryProvider>
            <LanguageSyncProvider>
              <NuqsAdapter>{children}</NuqsAdapter>
            </LanguageSyncProvider>
          </ReactQueryProvider>
        </StoreProvider>
      </AccountProvider>
    </GoogleOAuthProvider>
  );
};
